import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSearch,
    faClipboardList,
    faChevronLeft,
    faChevronRight,
    faUser,
    faEnvelope,
    faClock,
    faCalendarAlt,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
    Card,
    Table,
    InputGroup,
    Form,
    Button,
    Badge,
    Modal,
    Row,
    Col,
} from "@themesberg/react-bootstrap";
import { toast } from "react-toastify";
import { adminGetActivityLogs, getActivityLogById } from "../../../pages/api";

const capitalize = (value) =>
    String(value || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const getPageItems = (pagination) => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
        return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
        return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
};

const getTypeBadgeStyle = (type) => {
    const logType = String(type || "").toLowerCase();

    if (logType.includes("course")) {
        return {
            background: "#2563eb",
            color: "#ffffff",
        };
    }

    if (logType.includes("user")) {
        return {
            background: "#7c3aed",
            color: "#ffffff",
        };
    }

    if (logType.includes("support")) {
        return {
            background: "#ea580c",
            color: "#ffffff",
        };
    }

    if (logType.includes("payment") || logType.includes("transaction")) {
        return {
            background: "#059669",
            color: "#ffffff",
        };
    }

    return {
        background: "#64748b",
        color: "#ffffff",
    };
};

const getChangeValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "N/A";
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return capitalize(value);
};

const renderChanges = (changes) => {
    if (!changes || typeof changes !== "object") {
        return <span className="text-muted">No changes available</span>;
    }

    const changeEntries = Object.entries(changes);

    if (changeEntries.length === 0) {
        return <span className="text-muted">No changes available</span>;
    }

    return (
        <div className="admin-log-changes">
            {changeEntries.map(([field, value]) => {
                const oldValue =
                    value && typeof value === "object" ? value.old : undefined;

                const newValue =
                    value && typeof value === "object" ? value.new : undefined;

                return (
                    <div
                        key={field}
                        className="admin-log-change-item"
                    >
                        <span className="admin-log-change-field">
                            {capitalize(field)}
                        </span>

                        <span className="admin-log-change-values">
                            <span className="admin-log-old-value">
                                {getChangeValue(oldValue)}
                            </span>

                            <span className="admin-log-change-arrow">→</span>

                            <span className="admin-log-new-value">
                                {getChangeValue(newValue)}
                            </span>
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const emptyActivityLog = {
    id: null,
    user: {},
    type: "",
    title: "",
    message: "",
    meta: {},
    created_at: "",
    time: "",
};

export default function AdminLog() {
    const [search, setSearch] = useState("");
    const [activityLogs, setActivityLogs] = useState([]);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedActivityLog, setSelectedActivityLog] = useState(emptyActivityLog);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const [loading, setLoading] = useState(false);

    const searchDebounceRef = useRef(null);
    const firstSearchRenderRef = useRef(true);

    const fetchActivityLogs = async (
        page = 1,
        perPage = 10,
        searchTerm = ""
    ) => {
        try {
            setLoading(true);

            const response = await adminGetActivityLogs({
                page,
                per_page: perPage,
                search: searchTerm,
            });

            const responseData = response?.data || {};

            const apiLogs = Array.isArray(responseData?.data)
                ? responseData.data
                : [];

            const apiPagination = responseData?.pagination || {};

            setActivityLogs(apiLogs);

            setPagination({
                current_page:
                    Number(apiPagination?.current_page ?? page) || page,

                last_page:
                    Number(apiPagination?.last_page ?? 1) || 1,

                per_page:
                    Number(apiPagination?.per_page ?? perPage) || perPage,

                total:
                    Number(apiPagination?.total ?? apiLogs.length) ||
                    apiLogs.length,
            });
        } catch (error) {
            console.error("Failed to fetch activity logs:", error);

            toast.error(
                error?.response?.data?.message ||
                "Unable to fetch activity logs."
            );

            setActivityLogs([]);

            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivityLogs(1,10, "");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (firstSearchRenderRef.current) {
            firstSearchRenderRef.current = false;
            return;
        }

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
            fetchActivityLogs(
                1,
                pagination.per_page,
                search.trim()
            );
        }, 300);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const clearSearchDebounce = () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) {
            return {
                start: 0,
                end: 0,
            };
        }

        const start = (current - 1) * perPage + 1;

        const end = Math.min(
            current * perPage,
            total
        );

        return {
            start,
            end,
        };
    };

    const {
        start,
        end,
    } = getShowingRange();



    const fetchActivityLogById = async (id) => {
        setDetailLoading(true);
        try {
            const response = await getActivityLogById(id);
            const data = response?.data?.data || emptyActivityLog;

            setSelectedActivityLog({
                id: data?.id,
                user: data?.user || {},
                type: data?.type || "",
                title: data?.title || "",
                message: data?.message || "",
                meta: data?.meta || {},
                created_at: data?.created_at || "",
                time: data?.time || "",
            });

            return data;
        } catch (error) {
            // console.error("Failed to fetch log details:", error);
            toast.error("Unable to load log details.");
            return null;
        } finally {
            setDetailLoading(false);
        }
    };


    const openViewModal = async (id) => {
        const data = await fetchActivityLogById(id);
        if (data) setViewModalOpen(true);
    };

    return (
        <>
            <div className="admin-log-page">

                {/* Header */}
                <div className="admin-log-header py-4">

                    <div className="admin-log-heading-wrap">
                        <h2 className="h4 mb-1 admin-log-title">
                            Activity Logs
                        </h2>
                    </div>

                    <div className="admin-log-header-actions">

                        <InputGroup className="admin-log-search">

                            <InputGroup.Text className="bg-white">
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className="text-muted"
                                />
                            </InputGroup.Text>

                            <Form.Control
                                type="text"
                                placeholder="Search"
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                                style={{
                                    boxShadow: "none",
                                }}
                            />

                        </InputGroup>

                    </div>

                </div>

                {/* Main Card */}
                <Card className="border-0 shadow-sm rounded-4">

                    <Card.Body className="p-4">

                        <div className="admin-log-list-top">

                            <div className="admin-log-list-title-wrap">

                                <h5 className="mb-1">
                                    All Activity Logs
                                </h5>

                                <div className="text-muted small">
                                    View all administrative activities and changes.
                                </div>

                            </div>

                        </div>

                        {/* Table */}
                        <div className="table-responsive">

                            <Table
                                hover
                                className="align-middle mb-0 admin-log-table"
                            >

                                <thead>

                                    <tr>

                                        <th className="fw-semibold">
                                            Sr No
                                        </th>

                                        <th>
                                            User
                                        </th>

                                        <th>
                                            Activity
                                        </th>

                                        <th>
                                            Message
                                        </th>

                                        {/* <th>
                                            Changes
                                        </th> */}

                                        <th>Date & Time</th>
                                        <th className="text-center">Action</th>

                                    </tr>

                                </thead>

                                <tbody>

                                    {loading ? (

                                        <tr>

                                            <td
                                                colSpan={6}
                                                className="text-center text-muted py-5"
                                            >
                                                Loading activity logs...
                                            </td>

                                        </tr>

                                    ) : activityLogs.length > 0 ? (

                                        activityLogs.map(
                                            (item, index) => (

                                                <tr key={item?.id}>

                                                    {/* Sr No */}
                                                    <td className="fw-semibold">
                                                        {start + index}
                                                    </td>

                                                    {/* Admin */}
                                                    <td>

                                                        <div className="admin-log-user">

                                                            {/* <div className="admin-log-user-icon">

                                <FontAwesomeIcon
                                  icon={faUser}
                                />

                              </div> */}

                                                            <div className="admin-log-user-details">

                                                                <div className="fw-semibold">
                                                                    {item?.user?.name ||
                                                                        "N/A"}
                                                                </div>

                                                                <div className="admin-log-email">

                                                                    <FontAwesomeIcon
                                                                        icon={faEnvelope}
                                                                        className="me-1"
                                                                    />

                                                                    {item?.user?.email ||
                                                                        "N/A"}

                                                                </div>

                                                            </div>

                                                        </div>

                                                    </td>

                                                    {/* Activity */}
                                                    <td>

                                                        <div className="admin-log-activity">

                                                            <div className="fw-semibold mb-2">

                                                                {item?.title ||
                                                                    capitalize(
                                                                        item?.type
                                                                    )}

                                                            </div>

                                                            {/* <Badge
                                className="rounded-pill"
                                style={{
                                  ...getTypeBadgeStyle(
                                    item?.type
                                  ),

                                  padding:
                                    "0.45rem 0.75rem",

                                  fontWeight:
                                    500,
                                }}
                              >

                                {capitalize(
                                  item?.type
                                )}

                              </Badge> */}

                                                        </div>

                                                    </td>

                                                    {/* Message */}
                                                    <td>

                                                        <div className="admin-log-message">

                                                            {item?.message ||
                                                                "N/A"}

                                                        </div>

                                                    </td>

                                                    {/* Changes */}
                                                    {/* <td>

                                                        {renderChanges(
                                                            item?.meta?.changes
                                                        )}

                                                    </td> */}

                                                    {/* Date & Time */}
                                                    <td>
                                                        <div className="admin-log-date-time">
                                                            <div className="admin-log-date">
                                                                <FontAwesomeIcon
                                                                    icon={faCalendarAlt}
                                                                    className="me-2 text-muted"
                                                                />

                                                                {item?.created_at || "N/A"}
                                                            </div>

                                                            <div className="admin-log-time">
                                                                <FontAwesomeIcon
                                                                    icon={faClock}
                                                                    className="me-2 text-muted"
                                                                />

                                                                {item?.time || "N/A"}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-2 flex-nowrap">
                                                            <Button
                                                                type="button"
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => openViewModal(item.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                                {/* View */}
                                                            </Button>
                                                        </div>
                                                    </td>

                                                </tr>

                                            )
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan={6}
                                                className="text-center text-muted py-5"
                                            >

                                                {/* <FontAwesomeIcon
                                                    icon={faClipboardList}
                                                    className="d-block mx-auto mb-3"
                                                    style={{
                                                        fontSize: "2rem",
                                                        opacity: 0.35,
                                                    }}
                                                /> */}

                                                No activity logs found.

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </Table>

                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">

                            <div className="text-muted small">

                                Showing {start} to {end} of{" "}
                                {pagination.total} entries

                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap">

                                {/* Previous */}
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={
                                        pagination.current_page <= 1 ||
                                        loading
                                    }
                                    onClick={() => {
                                        clearSearchDebounce();

                                        fetchActivityLogs(
                                            Math.max(
                                                1,
                                                pagination.current_page - 1
                                            ),

                                            pagination.per_page,

                                            search.trim()
                                        );
                                    }}
                                    className="course-page-btn"
                                >

                                    <FontAwesomeIcon
                                        icon={faChevronLeft}
                                    />

                                </Button>

                                {/* Page Numbers */}
                                {getPageItems(
                                    pagination
                                ).map(
                                    (item, index) =>

                                        item === "..." ? (

                                            <span
                                                key={`dots-${index}`}
                                                className="text-muted px-1"
                                            >
                                                ...
                                            </span>

                                        ) : (

                                            <Button
                                                key={item}
                                                type="button"
                                                variant="outline-primary"
                                                disabled={loading}
                                                onClick={() => {

                                                    clearSearchDebounce();

                                                    fetchActivityLogs(
                                                        item,

                                                        pagination.per_page,

                                                        search.trim()
                                                    );

                                                }}
                                                className={`course-page-btn ${item ===
                                                    pagination.current_page
                                                    ? "course-page-btn-active"
                                                    : ""
                                                    }`}
                                            >

                                                {item}

                                            </Button>

                                        )

                                )}

                                {/* Next */}
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    disabled={
                                        pagination.current_page >=
                                        pagination.last_page ||
                                        loading
                                    }
                                    onClick={() => {

                                        clearSearchDebounce();

                                        fetchActivityLogs(
                                            Math.min(
                                                pagination.last_page,

                                                pagination.current_page +
                                                1
                                            ),

                                            pagination.per_page,

                                            search.trim()
                                        );

                                    }}
                                    className="course-page-btn"
                                >

                                    <FontAwesomeIcon
                                        icon={faChevronRight}
                                    />

                                </Button>

                            </div>

                        </div>

                    </Card.Body>

                </Card>

            </div>

            <Modal
                show={viewModalOpen}
                onHide={() => setViewModalOpen(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">
                        Activity Log Details
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    {detailLoading ? (
                        <div className="text-center py-5 text-muted">
                            Loading activity details...
                        </div>
                    ) : (
                        <>


                            <Card className="border shadow-sm rounded-4 mb-4 pb-0">
                                <Card.Body className="px-3">

                                    <div className="d-flex align-items-start">
                                        <div className="flex-grow-1">

                                            {/* One Row */}
                                            <Row className="g-3 align-items-start mb-3">

                                                <Col xs={12} sm={6} lg={4}>
                                                    <small className="text-muted d-block">
                                                        User
                                                    </small>
                                                    <div className="fw-semibold">
                                                        {selectedActivityLog.user?.name || "N/A"}
                                                    </div>
                                                    <div className="text-muted small mb-1">
                                                        {selectedActivityLog.user?.email || "N/A"}
                                                    </div>
                                                </Col>

                                                <Col xs={12} sm={6} lg={4}>
                                                    <small className="text-muted d-block">
                                                        Activity
                                                    </small>
                                                    <div className="fw-semibold">
                                                        {selectedActivityLog.title}
                                                    </div>
                                                </Col>

                                                <Col xs={12} lg={4}>
                                                    <small className="text-muted d-block">
                                                        Date & Time
                                                    </small>
                                                    <div className="fw-semibold">
                                                        {selectedActivityLog.created_at}
                                                    </div>
                                                    <small className="text-muted">
                                                        {selectedActivityLog.time}
                                                    </small>
                                                </Col>

                                            </Row>

                                            <hr />

                                            {/* Message */}
                                            <div>
                                                <small className="text-muted d-block mb-1">
                                                    Message
                                                </small>

                                                <div className="text-primary fw-medium">
                                                    {selectedActivityLog.message}
                                                </div>
                                            </div>

                                        </div>

                                    </div>

                                </Card.Body>
                            </Card>

                            {selectedActivityLog.meta?.changes && (
                                <Card className="border shadow-sm rounded-4">
                                    <Card.Body className="px-3">

                                        <h6 className="fw-bold mb-4">
                                            Changes
                                        </h6>

                                        <div className="table-responsive">

                                            <table className="table align-middle mb-0">

                                                <thead className="table-light">

                                                    <tr>
                                                        <th style={{ width: "25%" }}>
                                                            Field
                                                        </th>

                                                        <th>
                                                            Previous Value
                                                        </th>

                                                        <th>
                                                            Updated Value
                                                        </th>
                                                    </tr>

                                                </thead>

                                                <tbody>

                                                    {Object.entries(
                                                        selectedActivityLog.meta.changes
                                                    ).map(([field, value]) => (

                                                        <tr key={field}>

                                                            <td className="fw-semibold">
                                                                {capitalize(field)}
                                                            </td>

                                                            <td>
                                                                <span
                                                                    className="text-danger text-decoration-line-through fw-medium"
                                                                    style={{ wordBreak: "break-word" }}
                                                                >
                                                                    {getChangeValue(value.old)}
                                                                </span>
                                                            </td>

                                                            <td>
                                                                <span
                                                                    className="text-success fw-semibold"
                                                                    style={{ wordBreak: "break-word" }}
                                                                >
                                                                    {getChangeValue(value.new)}
                                                                </span>
                                                            </td>

                                                        </tr>

                                                    ))}

                                                </tbody>

                                            </table>

                                        </div>

                                    </Card.Body>
                                </Card>
                            )}

                        </>
                    )}

                </Modal.Body>

            </Modal>

            <style>{`

        .admin-log-page {
          width: 100%;
          padding-inline: 0;
        }

        .admin-log-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .admin-log-heading-wrap {
          min-width: 0;
          flex: 1 1 auto;
        }

        .admin-log-title {
          font-size: clamp(
            1.4rem,
            2vw,
            2rem
          );

          line-height: 1.2;

          word-break: break-word;
        }

        .admin-log-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          flex: 0 0 auto;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .admin-log-search {
          width: 100%;
          max-width: 550px;
        }

        .admin-log-search
        .input-group-text,

        .admin-log-search
        .form-control {

          min-height: 48px;
          height: 48px;

        }

        .admin-log-search
        .input-group-text {

          display: flex;
          align-items: center;

        }

        .admin-log-list-top {

          display: flex;

          justify-content:
            space-between;

          align-items:
            center;

          gap: 1rem;

          margin-bottom:
            1.25rem;

        }

        .admin-log-list-title-wrap {

          min-width: 0;

          flex: 1 1 auto;

        }

        .admin-log-table
        thead
        th {

          white-space:
            nowrap;

          background:
            #f8fafc;

          font-weight:
            600;

        }

        .admin-log-table
        td {

          vertical-align:
            middle;

        }

        .admin-log-user {

          display:
            flex;

          align-items:
            center;

          gap:
            0.75rem;

          min-width:
            190px;

        }

        .admin-log-user-icon {

          width:
            42px;

          height:
            42px;

          min-width:
            42px;

          border-radius:
            50%;

          display:
            flex;

          align-items:
            center;

          justify-content:
            center;

          background:
            #eaf2ff;

          color:
            #1d4e89;

        }

        .admin-log-user-details {

          min-width:
            0;

        }

        .admin-log-email {

          color:
            #6b7280;

          font-size:
            0.8rem;

          margin-top:
            0.15rem;

          overflow:
            hidden;

          text-overflow:
            ellipsis;

          white-space:
            nowrap;

          max-width:
            210px;

        }

        .admin-log-activity {

          min-width:
            180px;

        }

        .admin-log-message {

          min-width:
            240px;

          max-width:
            350px;

          white-space:
            normal;

          line-height:
            1.5;

          color:
            #475569;

          word-break:
            break-word;

        }

        .admin-log-changes {

          min-width:
            220px;

          max-width:
            320px;

          display:
            flex;

          flex-direction:
            column;

          gap:
            0.45rem;

        }

        .admin-log-change-item {

          display:
            flex;

          flex-direction:
            column;

          gap:
            0.3rem;

          padding:
            0.55rem 0.65rem;

          border:
            1px solid
            rgba(
              0,
              0,
              0,
              0.06
            );

          border-radius:
            0.55rem;

          background:
            #f8fafc;

        }

        .admin-log-change-field {

          font-size:
            0.75rem;

          font-weight:
            600;

          color:
            #64748b;

        }

        .admin-log-change-values {

          display:
            flex;

          align-items:
            center;

          flex-wrap:
            wrap;

          gap:
            0.4rem;

          font-size:
            0.8rem;

        }

        .admin-log-old-value {

          color:
            #dc2626;

          text-decoration:
            line-through;

        }

        .admin-log-change-arrow {

          color:
            #64748b;

          font-weight:
            600;

        }

        .admin-log-new-value {

          color:
            #059669;

          font-weight:
            600;

        }

        .admin-log-date,

        .admin-log-time {

          white-space:
            nowrap;

          color:
            #475569;

          font-size:
            0.875rem;

        }

        @media (
          max-width:
          991.98px
        ) {

          .admin-log-table {

            min-width:
              1250px;

          }

        }

        @media (
          max-width:
          767.98px
        ) {

          .admin-log-header {

            flex-wrap:
              wrap;

          }

          .admin-log-heading-wrap {

            flex:
              1 1 100%;

            width:
              100%;

          }

          .admin-log-header-actions {

            width:
              100%;

            justify-content:
              flex-end;

          }

          .admin-log-search {

            max-width:
              none;

            width:
              100%;

          }

          .admin-log-list-top {

            align-items:
              flex-start;

            flex-wrap:
              wrap;

          }

          .admin-log-table {

            min-width:
              1150px;

          }

        }

        .admin-log-date-time {
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.admin-log-date,
.admin-log-time {
  display: flex;
  align-items: center;
  white-space: nowrap;
  color: #475569;
  font-size: 0.875rem;
}

.admin-log-date {
  font-weight: 600;
}

.admin-log-time {
  color: #64748b;
  font-size: 0.8rem;
}

        @media (
          max-width:
          575.98px
        ) {

          .admin-log-header {

            padding-top:
              0.5rem !important;

            padding-bottom:
              1rem !important;

          }

          .admin-log-title {

            font-size:
              1.35rem;

          }

          .admin-log-search
          .input-group-text,

          .admin-log-search
          .form-control {

            min-height:
              42px;

            height:
              42px;

          }

          .admin-log-list-top {

            margin-bottom:
              1rem;

          }

          .admin-log-user {

            min-width:
              180px;

          }

        }

      `}</style>
        </>
    );
}